import { useCallback, useState } from 'react';

type DialogCloseReason = 'backdropClick' | 'escapeKeyDown';

export type DiscardChangesConfirmProps = {
    open: boolean;
    onKeepEditing: () => void;
    onDiscard: () => void;
};

export type DiscardChangesGuard<E> = {
    /** For a close button, Cancel or an Escape hotkey: asks first while dirty. */
    requestClose: (event?: E) => void;
    /** For MUI Dialog's `onClose`: a backdrop click is ignored while dirty, Escape asks first. */
    onDialogClose: (event: unknown, reason?: DialogCloseReason) => void;
    /** Spread onto `DiscardChangesDialog`. */
    confirmProps: DiscardChangesConfirmProps;
};

/**
 * Keeps a dirty form from being thrown away by a stray Escape or backdrop click. Not dirty, every
 * close goes straight to `onClose`, as before.
 */
export const useDiscardChangesGuard = <E = unknown>(
    isDirty: boolean,
    onClose: ((event?: E) => void) | null | undefined,
): DiscardChangesGuard<E> => {
    const [confirmOpen, setConfirmOpen] = useState(false);

    const requestClose = useCallback(
        (event?: E) => {
            if (isDirty) {
                setConfirmOpen(true);
                return;
            }
            onClose?.(event);
        },
        [isDirty, onClose],
    );

    const onDialogClose = useCallback(
        (event: unknown, reason?: DialogCloseReason) => {
            if (isDirty && reason === 'backdropClick') return;
            requestClose(event as E);
        },
        [isDirty, requestClose],
    );

    const onKeepEditing = useCallback(() => setConfirmOpen(false), []);
    const onDiscard = useCallback(() => {
        setConfirmOpen(false);
        onClose?.();
    }, [onClose]);

    return {
        requestClose,
        onDialogClose,
        confirmProps: { open: confirmOpen, onKeepEditing, onDiscard },
    };
};
